import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  InputAdornment,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Code as CodeIcon,
  AccountTree as BranchIcon,
  PlayArrow as PipelineIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  Schedule as PendingIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon,
  CallMerge as MergeIcon,
  PlayCircleOutline as TriggerIcon,
} from '@mui/icons-material';
import {
  gitlabApi,
  GitLabInstance,
  GitLabProject,
  GitLabTreeItem,
  GitLabFileResult,
  GitLabBranch,
  GitLabPipeline,
} from '../services/gitlab-api';
import {
  parseTerraformResources,
  TerraformResource,
  RANGEOS_RESOURCE_TYPES,
  RESOURCE_KEY_FIELDS,
} from '../utils/terraform-parser';

const EDITABLE_EXTENSIONS = ['.tf', '.json', '.yaml', '.yml', '.md', '.txt', '.hcl', '.sh', '.toml', '.cfg', '.ini', '.xml', '.html', '.css', '.js', '.ts', '.py'];

function isEditableFile(filePath: string): boolean {
  return EDITABLE_EXTENSIONS.some(ext => filePath.toLowerCase().endsWith(ext));
}

// ============================================================================
// Add/Edit Instance Dialog
// ============================================================================

interface InstanceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  instance?: GitLabInstance | null;
}

const InstanceDialog: React.FC<InstanceDialogProps> = ({ open, onClose, onSave, instance }) => {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [authType, setAuthType] = useState<'pat' | 'project-token'>('pat');
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (instance) {
        setName(instance.name);
        setUrl(instance.url);
        setAuthType(instance.authType);
        setToken('');
      } else {
        setName('');
        setUrl('');
        setAuthType('pat');
        setToken('');
      }
      setError(null);
    }
  }, [open, instance]);

  const handleSave = async () => {
    if (!name || !url) {
      setError('Name and URL are required');
      return;
    }
    if (!instance && !token) {
      setError('Token is required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (instance) {
        const updates: any = { name, url, authType };
        if (token) updates.token = token;
        await gitlabApi.updateInstance(instance.id, updates);
      } else {
        await gitlabApi.addInstance({ name, url, authType, token });
      }
      onSave();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{instance ? 'Edit GitLab Instance' : 'Add GitLab Instance'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label="Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
          placeholder="e.g. Production GitLab"
        />
        <TextField
          label="URL"
          fullWidth
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          sx={{ mb: 2 }}
          placeholder="https://gitlab.example.com"
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Auth Type</InputLabel>
          <Select
            value={authType}
            label="Auth Type"
            onChange={(e) => setAuthType(e.target.value as 'pat' | 'project-token')}
          >
            <MenuItem value="pat">Personal Access Token</MenuItem>
            <MenuItem value="project-token">Project Token</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Token"
          fullWidth
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder={instance ? 'Leave blank to keep existing' : 'Enter token'}
          helperText={authType === 'pat' ? 'Personal Access Token with api scope' : 'Project-scoped access token'}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// Commit Dialog
// ============================================================================

interface CommitDialogProps {
  open: boolean;
  onClose: () => void;
  onCommit: (message: string, branch: string, newBranch?: string) => Promise<void>;
  currentBranch: string;
  filePath: string;
}

const CommitDialog: React.FC<CommitDialogProps> = ({ open, onClose, onCommit, currentBranch, filePath }) => {
  const [message, setMessage] = useState('');
  const [branchChoice, setBranchChoice] = useState<'current' | 'new'>('current');
  const [newBranchName, setNewBranchName] = useState('');
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMessage(`Update ${filePath.split('/').pop()}`);
      setBranchChoice('current');
      setNewBranchName('');
      setError(null);
    }
  }, [open, filePath]);

  const handleCommit = async () => {
    if (!message.trim()) {
      setError('Commit message is required');
      return;
    }
    if (branchChoice === 'new' && !newBranchName.trim()) {
      setError('Branch name is required');
      return;
    }
    setCommitting(true);
    setError(null);
    try {
      const branch = branchChoice === 'new' ? newBranchName.trim() : currentBranch;
      await onCommit(message.trim(), branch, branchChoice === 'new' ? newBranchName.trim() : undefined);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Commit Changes</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          File: {filePath}
        </Typography>
        <TextField
          label="Commit message"
          fullWidth
          multiline
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{ mb: 2 }}
        />
        <RadioGroup value={branchChoice} onChange={(e) => setBranchChoice(e.target.value as 'current' | 'new')}>
          <FormControlLabel
            value="current"
            control={<Radio size="small" />}
            label={<>Commit to <Chip size="small" label={currentBranch} icon={<BranchIcon />} variant="outlined" /></>}
          />
          <FormControlLabel
            value="new"
            control={<Radio size="small" />}
            label="Create new branch"
          />
        </RadioGroup>
        {branchChoice === 'new' && (
          <TextField
            label="New branch name"
            fullWidth
            size="small"
            value={newBranchName}
            onChange={(e) => setNewBranchName(e.target.value)}
            sx={{ mt: 1, ml: 4 }}
            placeholder="feature/my-changes"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleCommit} variant="contained" disabled={committing} startIcon={<SaveIcon />}>
          {committing ? <CircularProgress size={20} /> : 'Commit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// Create Branch Dialog
// ============================================================================

interface CreateBranchDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
  sourceBranch: string;
}

const CreateBranchDialog: React.FC<CreateBranchDialogProps> = ({ open, onClose, onCreate, sourceBranch }) => {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setError(null);
    }
  }, [open]);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Branch name is required');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await onCreate(name.trim());
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Branch</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          From: <Chip size="small" label={sourceBranch} icon={<BranchIcon />} variant="outlined" />
        </Typography>
        <TextField
          label="Branch name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 1 }}
          placeholder="feature/my-branch"
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" disabled={creating}>
          {creating ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// Merge Request Dialog
// ============================================================================

interface MergeRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (title: string, targetBranch: string) => Promise<string>;
  sourceBranch: string;
  branches: GitLabBranch[];
  defaultBranch: string;
}

const MergeRequestDialog: React.FC<MergeRequestDialogProps> = ({ open, onClose, onCreate, sourceBranch, branches, defaultBranch }) => {
  const [title, setTitle] = useState('');
  const [targetBranch, setTargetBranch] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mrUrl, setMrUrl] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle('');
      setTargetBranch(defaultBranch);
      setError(null);
      setMrUrl(null);
    }
  }, [open, defaultBranch]);

  const handleCreate = async () => {
    if (!title.trim()) {
      setError('MR title is required');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const url = await onCreate(title.trim(), targetBranch);
      setMrUrl(url);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Merge Request</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {mrUrl && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Merge request created: <Link href={mrUrl} target="_blank" rel="noopener">{mrUrl}</Link>
          </Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Source:</Typography>
          <Chip size="small" label={sourceBranch} icon={<BranchIcon />} variant="outlined" />
        </Box>
        <TextField
          label="Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
          autoFocus
          disabled={!!mrUrl}
        />
        <FormControl fullWidth size="small">
          <InputLabel>Target Branch</InputLabel>
          <Select
            value={targetBranch}
            label="Target Branch"
            onChange={(e) => setTargetBranch(e.target.value)}
            disabled={!!mrUrl}
          >
            {branches.filter(b => b.name !== sourceBranch).map(b => (
              <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{mrUrl ? 'Close' : 'Cancel'}</Button>
        {!mrUrl && (
          <Button onClick={handleCreate} variant="contained" disabled={creating} startIcon={<MergeIcon />}>
            {creating ? <CircularProgress size={20} /> : 'Create MR'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// Pipeline Status Chip
// ============================================================================

const PipelineStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const statusConfig: Record<string, { color: 'success' | 'error' | 'warning' | 'default' | 'info'; icon: React.ReactElement }> = {
    success: { color: 'success', icon: <SuccessIcon fontSize="small" /> },
    failed: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
    running: { color: 'info', icon: <PipelineIcon fontSize="small" /> },
    pending: { color: 'warning', icon: <PendingIcon fontSize="small" /> },
    canceled: { color: 'default', icon: <CancelIcon fontSize="small" /> },
    skipped: { color: 'default', icon: <CancelIcon fontSize="small" /> },
    created: { color: 'default', icon: <PendingIcon fontSize="small" /> },
  };

  const cfg = statusConfig[status] || { color: 'default' as const, icon: <PendingIcon fontSize="small" /> };
  return <Chip size="small" label={status} color={cfg.color} icon={cfg.icon} />;
};

// ============================================================================
// Terraform Resources Panel
// ============================================================================

const TerraformResourcesPanel: React.FC<{ resources: TerraformResource[] }> = ({ resources }) => {
  if (resources.length === 0) return null;

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', maxHeight: 300, overflow: 'auto' }}>
      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <CodeIcon fontSize="small" color="secondary" />
        <Typography variant="subtitle2">Terraform Resources</Typography>
        <Chip size="small" label={resources.length} color="secondary" />
      </Box>
      {resources.map((r, idx) => {
        const isRangeOS = RANGEOS_RESOURCE_TYPES.includes(r.type);
        const keyFields = RESOURCE_KEY_FIELDS[r.type] || [];
        const priorityAttrs = r.attributes.filter(a => keyFields.includes(a.key));
        const s3Attrs = r.attributes.filter(a => a.isS3Reference && !keyFields.includes(a.key));
        const displayAttrs = [...priorityAttrs, ...s3Attrs];

        return (
          <Box key={idx} sx={{ px: 2, py: 1, borderBottom: idx < resources.length - 1 ? 1 : 0, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: displayAttrs.length > 0 ? 0.5 : 0 }}>
              <Chip
                size="small"
                label={r.type}
                color={isRangeOS ? 'secondary' : 'default'}
                variant={isRangeOS ? 'filled' : 'outlined'}
              />
              <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                L{r.lineStart}-{r.lineEnd}
              </Typography>
            </Box>
            {displayAttrs.length > 0 && (
              <Table size="small" sx={{ ml: 2, '& td': { py: 0.25, px: 1, border: 0, fontSize: '12px' } }}>
                <TableBody>
                  {displayAttrs.map((attr, ai) => (
                    <TableRow key={ai}>
                      <TableCell sx={{ color: 'text.secondary', fontFamily: 'monospace', width: 160 }}>
                        {attr.key}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {attr.isS3Reference && (
                          <Chip size="small" label="S3" sx={{ bgcolor: 'warning.main', color: 'warning.contrastText', height: 18, fontSize: '10px' }} />
                        )}
                        <span style={{ color: attr.isS3Reference ? '#ed6c02' : 'inherit' }}>{attr.value}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ============================================================================
// Main GitLabManager Component
// ============================================================================

export const GitLabManager: React.FC = () => {
  // Instance state
  const [instances, setInstances] = useState<GitLabInstance[]>([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>('');
  const [instanceDialogOpen, setInstanceDialogOpen] = useState(false);
  const [editingInstance, setEditingInstance] = useState<GitLabInstance | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testing, setTesting] = useState(false);

  // Project state
  const [projects, setProjects] = useState<GitLabProject[]>([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState<GitLabProject | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Branch state
  const [branches, setBranches] = useState<GitLabBranch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [createBranchOpen, setCreateBranchOpen] = useState(false);

  // Tree state
  const [treeItems, setTreeItems] = useState<GitLabTreeItem[]>([]);
  const [treePath, setTreePath] = useState<string>('');
  const [loadingTree, setLoadingTree] = useState(false);

  // File state
  const [selectedFile, setSelectedFile] = useState<GitLabFileResult | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);

  // Editor state
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [commitDialogOpen, setCommitDialogOpen] = useState(false);

  // MR state
  const [mrDialogOpen, setMrDialogOpen] = useState(false);

  // Pipeline state
  const [pipelines, setPipelines] = useState<GitLabPipeline[]>([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [triggeringPipeline, setTriggeringPipeline] = useState(false);

  // Terraform state
  const [terraformResources, setTerraformResources] = useState<TerraformResource[]>([]);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Load instances on mount
  useEffect(() => {
    loadInstances();
  }, []);

  const loadInstances = async () => {
    try {
      const data = await gitlabApi.listInstances();
      setInstances(data);
      if (data.length > 0 && !selectedInstanceId) {
        setSelectedInstanceId(data[0].id);
      }
    } catch (err: any) {
      setError('Failed to load GitLab instances');
    }
  };

  // Load projects when instance changes
  useEffect(() => {
    if (selectedInstanceId) {
      loadProjects();
    } else {
      setProjects([]);
      setSelectedProject(null);
    }
  }, [selectedInstanceId]);

  const loadProjects = async (search?: string) => {
    if (!selectedInstanceId) return;
    setLoadingProjects(true);
    setError(null);
    try {
      const data = await gitlabApi.listProjects(selectedInstanceId, search);
      setProjects(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  // Load branches + tree when project changes
  useEffect(() => {
    if (selectedProject && selectedInstanceId) {
      loadBranches();
      setTreePath('');
      setSelectedFile(null);
      setEditing(false);
      setTerraformResources([]);
      loadPipelines();
    } else {
      setBranches([]);
      setTreeItems([]);
      setSelectedFile(null);
      setEditing(false);
      setTerraformResources([]);
      setPipelines([]);
    }
  }, [selectedProject]);

  // Load tree when branch or path changes
  useEffect(() => {
    if (selectedProject && selectedInstanceId && selectedBranch) {
      loadTree();
    }
  }, [selectedBranch, treePath]);

  // Parse terraform when file changes
  useEffect(() => {
    if (selectedFile && selectedFile.fileMeta.file_path.endsWith('.tf')) {
      const result = parseTerraformResources(selectedFile.content);
      setTerraformResources(result.resources);
    } else {
      setTerraformResources([]);
    }
  }, [selectedFile]);

  // Reset editor on navigation
  useEffect(() => {
    setEditing(false);
  }, [selectedFile, selectedBranch, selectedProject]);

  const loadBranches = async () => {
    if (!selectedInstanceId || !selectedProject) return;
    try {
      const data = await gitlabApi.listBranches(selectedInstanceId, selectedProject.id);
      setBranches(data);
      const defaultBranch = data.find(b => b.default)?.name || data[0]?.name || '';
      setSelectedBranch(defaultBranch);
    } catch (err: any) {
      setError('Failed to load branches');
    }
  };

  const loadTree = async () => {
    if (!selectedInstanceId || !selectedProject) return;
    setLoadingTree(true);
    try {
      const data = await gitlabApi.browseTree(
        selectedInstanceId,
        selectedProject.id,
        treePath || undefined,
        selectedBranch || undefined,
      );
      // Sort: folders first, then files
      const sorted = [...data].sort((a, b) => {
        if (a.type === b.type) return a.name.localeCompare(b.name);
        return a.type === 'tree' ? -1 : 1;
      });
      setTreeItems(sorted);
    } catch (err: any) {
      setError('Failed to load repository tree');
    } finally {
      setLoadingTree(false);
    }
  };

  const loadPipelines = async () => {
    if (!selectedInstanceId || !selectedProject) return;
    setLoadingPipelines(true);
    try {
      const data = await gitlabApi.listPipelines(selectedInstanceId, selectedProject.id);
      setPipelines(data);
    } catch (err: any) {
      // Non-critical, just clear
      setPipelines([]);
    } finally {
      setLoadingPipelines(false);
    }
  };

  const handleTreeItemClick = async (item: GitLabTreeItem) => {
    if (item.type === 'tree') {
      setTreePath(item.path);
      setSelectedFile(null);
    } else {
      // Load file content
      setLoadingFile(true);
      try {
        const result = await gitlabApi.getFile(
          selectedInstanceId,
          selectedProject!.id,
          item.path,
          selectedBranch || undefined,
        );
        setSelectedFile(result);
      } catch (err: any) {
        setError('Failed to load file content');
      } finally {
        setLoadingFile(false);
      }
    }
  };

  const handleNavigateUp = () => {
    if (!treePath) return;
    const parts = treePath.split('/');
    parts.pop();
    setTreePath(parts.join('/'));
    setSelectedFile(null);
  };

  const handleTestConnection = async () => {
    if (!selectedInstanceId) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await gitlabApi.testConnection(selectedInstanceId);
      setTestResult(result);
    } catch (err: any) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleDeleteInstance = async (id: string) => {
    if (!confirm('Delete this GitLab instance?')) return;
    try {
      await gitlabApi.deleteInstance(id);
      if (selectedInstanceId === id) {
        setSelectedInstanceId('');
        setSelectedProject(null);
      }
      loadInstances();
    } catch (err: any) {
      setError('Failed to delete instance');
    }
  };

  const handleStartEdit = () => {
    if (selectedFile) {
      setEditContent(selectedFile.content);
      setEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditContent('');
  };

  const handleCommit = async (message: string, branch: string, newBranch?: string) => {
    if (!selectedFile || !selectedProject) return;
    // If committing to a new branch, create it first
    if (newBranch) {
      await gitlabApi.createBranch(selectedInstanceId, selectedProject.id, newBranch, selectedBranch);
    }
    await gitlabApi.createOrUpdateFile(
      selectedInstanceId,
      selectedProject.id,
      selectedFile.fileMeta.file_path,
      editContent,
      branch,
      message,
      'update',
    );
    setEditing(false);
    // If new branch was created, reload branches and switch to it
    if (newBranch) {
      const data = await gitlabApi.listBranches(selectedInstanceId, selectedProject.id);
      setBranches(data);
      setSelectedBranch(newBranch);
    } else {
      // Reload file to get updated content
      const result = await gitlabApi.getFile(
        selectedInstanceId,
        selectedProject.id,
        selectedFile.fileMeta.file_path,
        branch,
      );
      setSelectedFile(result);
    }
  };

  const handleCreateBranch = async (name: string) => {
    if (!selectedProject) return;
    await gitlabApi.createBranch(selectedInstanceId, selectedProject.id, name, selectedBranch);
    const data = await gitlabApi.listBranches(selectedInstanceId, selectedProject.id);
    setBranches(data);
    setSelectedBranch(name);
  };

  const handleCreateMR = async (title: string, targetBranch: string): Promise<string> => {
    if (!selectedProject) throw new Error('No project selected');
    const result = await gitlabApi.createMergeRequest(
      selectedInstanceId,
      selectedProject.id,
      selectedBranch,
      targetBranch,
      title,
    );
    return result.web_url;
  };

  const handleTriggerPipeline = async () => {
    if (!selectedProject || !selectedBranch) return;
    if (!confirm(`Trigger pipeline on branch "${selectedBranch}"?`)) return;
    setTriggeringPipeline(true);
    try {
      await gitlabApi.triggerPipeline(selectedInstanceId, selectedProject.id, selectedBranch);
      await loadPipelines();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to trigger pipeline');
    } finally {
      setTriggeringPipeline(false);
    }
  };

  const selectedInstance = instances.find(i => i.id === selectedInstanceId);
  const defaultBranch = branches.find(b => b.default)?.name || '';
  const isOnDefaultBranch = selectedBranch === defaultBranch;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top bar: Instance selector + controls */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>GitLab Instance</InputLabel>
            <Select
              value={selectedInstanceId}
              label="GitLab Instance"
              onChange={(e) => {
                setSelectedInstanceId(e.target.value);
                setSelectedProject(null);
                setTreePath('');
                setSelectedFile(null);
                setEditing(false);
              }}
            >
              {instances.map(inst => (
                <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => { setEditingInstance(null); setInstanceDialogOpen(true); }}
          >
            Add
          </Button>

          {selectedInstance && (
            <>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => { setEditingInstance(selectedInstance); setInstanceDialogOpen(true); }}
              >
                Edit
              </Button>
              <Button
                size="small"
                startIcon={<DeleteIcon />}
                color="error"
                onClick={() => handleDeleteInstance(selectedInstanceId)}
              >
                Delete
              </Button>
              <Button
                size="small"
                onClick={handleTestConnection}
                disabled={testing}
              >
                {testing ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                Test Connection
              </Button>
            </>
          )}

          {testResult && (
            <Chip
              size="small"
              label={testResult.message}
              color={testResult.success ? 'success' : 'error'}
              onDelete={() => setTestResult(null)}
            />
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!selectedInstanceId ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {instances.length === 0
              ? 'No GitLab instances configured. Click "Add" to get started.'
              : 'Select a GitLab instance to browse repositories.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left panel: Project selector + file tree */}
          <Box sx={{ width: 360, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Project selector */}
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Autocomplete
                size="small"
                options={projects}
                getOptionLabel={(p) => p.path_with_namespace}
                value={selectedProject}
                onChange={(_, newValue) => setSelectedProject(newValue)}
                loading={loadingProjects}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    setProjectSearch(value);
                    loadProjects(value || undefined);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project"
                    placeholder="Search projects..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.path_with_namespace}</Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
              />

              {selectedProject && branches.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Branch</InputLabel>
                    <Select
                      value={selectedBranch}
                      label="Branch"
                      onChange={(e) => {
                        setSelectedBranch(e.target.value);
                        setTreePath('');
                        setSelectedFile(null);
                        setEditing(false);
                      }}
                    >
                      {branches.map(b => (
                        <MenuItem key={b.name} value={b.name}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BranchIcon fontSize="small" />
                            {b.name}
                            {b.default && <Chip size="small" label="default" variant="outlined" sx={{ ml: 1 }} />}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title="Create branch">
                    <IconButton size="small" onClick={() => setCreateBranchOpen(true)}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!isOnDefaultBranch && (
                    <Tooltip title="Create merge request">
                      <IconButton size="small" onClick={() => setMrDialogOpen(true)} color="primary">
                        <MergeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>

            {/* File tree */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loadingTree ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : selectedProject ? (
                <List dense disablePadding>
                  {treePath && (
                    <ListItem disablePadding>
                      <ListItemButton onClick={handleNavigateUp}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <BackIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary=".." />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {treeItems.map(item => (
                    <ListItem key={item.path} disablePadding>
                      <ListItemButton
                        onClick={() => handleTreeItemClick(item)}
                        selected={selectedFile?.fileMeta.file_path === item.path}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {item.type === 'tree' ? (
                            <FolderIcon fontSize="small" color="primary" />
                          ) : item.name.endsWith('.tf') ? (
                            <CodeIcon fontSize="small" color="secondary" />
                          ) : (
                            <FileIcon fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: item.name.endsWith('.tf') ? 600 : 400,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {treeItems.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="Empty directory"
                        primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                      />
                    </ListItem>
                  )}
                </List>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Select a project to browse files
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Current path breadcrumb */}
            {treePath && (
              <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  /{treePath}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right panel: File viewer + terraform + pipelines */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* File viewer */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loadingFile ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : selectedFile ? (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* File header */}
                  <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CodeIcon fontSize="small" color="secondary" />
                    <Typography variant="body2" fontWeight={600}>
                      {selectedFile.fileMeta.file_path}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedFile.fileMeta.size} bytes
                    </Typography>
                    <Chip
                      size="small"
                      label={selectedFile.fileMeta.ref}
                      icon={<BranchIcon />}
                      variant="outlined"
                    />
                    <Box sx={{ flex: 1 }} />
                    {isEditableFile(selectedFile.fileMeta.file_path) && !editing && (
                      <Tooltip title="Edit file">
                        <IconButton size="small" onClick={handleStartEdit}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {editing && (
                      <>
                        <Button size="small" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={() => setCommitDialogOpen(true)}
                        >
                          Save & Commit
                        </Button>
                      </>
                    )}
                  </Box>
                  {/* File content */}
                  <Box
                    sx={{
                      flex: 1,
                      overflow: 'auto',
                      p: 0,
                      bgcolor: 'grey.900',
                    }}
                  >
                    {editing ? (
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        spellCheck={false}
                        style={{
                          width: '100%',
                          height: '100%',
                          margin: 0,
                          padding: '16px',
                          fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
                          fontSize: '13px',
                          lineHeight: 1.5,
                          color: '#e0e0e0',
                          backgroundColor: '#1a1a2e',
                          border: 'none',
                          outline: 'none',
                          resize: 'none',
                          tabSize: 2,
                          boxSizing: 'border-box',
                        }}
                      />
                    ) : (
                      <pre
                        style={{
                          margin: 0,
                          padding: '16px',
                          fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
                          fontSize: '13px',
                          lineHeight: 1.5,
                          color: '#e0e0e0',
                          whiteSpace: 'pre',
                          overflowX: 'auto',
                          tabSize: 2,
                        }}
                      >
                        {selectedFile.content}
                      </pre>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {selectedProject
                      ? 'Select a file from the tree to view its contents'
                      : 'Select a project and file to view'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Terraform resources panel */}
            <TerraformResourcesPanel resources={terraformResources} />

            {/* Pipeline status section */}
            {selectedProject && (
              <Box sx={{ borderTop: 1, borderColor: 'divider', maxHeight: 200, overflow: 'auto' }}>
                <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                  <PipelineIcon fontSize="small" />
                  <Typography variant="subtitle2">Pipelines</Typography>
                  <Tooltip title="Refresh pipelines">
                    <IconButton size="small" onClick={loadPipelines} disabled={loadingPipelines}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Trigger pipeline">
                    <IconButton
                      size="small"
                      onClick={handleTriggerPipeline}
                      disabled={triggeringPipeline || !selectedBranch}
                      color="primary"
                    >
                      {triggeringPipeline ? <CircularProgress size={16} /> : <TriggerIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  {loadingPipelines && <CircularProgress size={16} />}
                </Box>
                {pipelines.length > 0 ? (
                  <List dense disablePadding>
                    {pipelines.slice(0, 10).map(p => (
                      <ListItem key={p.id} sx={{ py: 0.5, px: 2 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PipelineStatusChip status={p.status} />
                              <Typography variant="body2">#{p.iid}</Typography>
                              <Chip size="small" label={p.ref} variant="outlined" icon={<BranchIcon />} />
                              <Typography variant="caption" color="text.secondary">
                                {p.source}
                              </Typography>
                            </Box>
                          }
                          secondary={new Date(p.created_at).toLocaleString()}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1, pl: 2 }}>
                    No pipelines found
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Dialogs */}
      <InstanceDialog
        open={instanceDialogOpen}
        onClose={() => setInstanceDialogOpen(false)}
        onSave={loadInstances}
        instance={editingInstance}
      />
      <CommitDialog
        open={commitDialogOpen}
        onClose={() => setCommitDialogOpen(false)}
        onCommit={handleCommit}
        currentBranch={selectedBranch}
        filePath={selectedFile?.fileMeta.file_path || ''}
      />
      <CreateBranchDialog
        open={createBranchOpen}
        onClose={() => setCreateBranchOpen(false)}
        onCreate={handleCreateBranch}
        sourceBranch={selectedBranch}
      />
      <MergeRequestDialog
        open={mrDialogOpen}
        onClose={() => setMrDialogOpen(false)}
        onCreate={handleCreateMR}
        sourceBranch={selectedBranch}
        branches={branches}
        defaultBranch={defaultBranch}
      />
    </Box>
  );
};
